import { zodResolver } from '@hookform/resolvers/zod';
import { message } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';

import MainButton from '@/components/UI/mobile/MainButton';
import axiosWithAuth from '@/lib/axios';
import { useOrientation } from '@/lib/hooks/useOrientation';
import { asyncErrorHandler } from '@/utils/asyncErrorHandler';

import { type TodoItem, TodoItemSchema } from '../../types';
import HourAndMinuteSelector from './components/HourAndMinuteSelector';
import RepeatTimeSelector from './components/RepeatTimeSelector';
import { formatRemindTime, getDisplayRepeatTimes } from './helpers';
import styles from './index.module.scss';

type AddAndEditTodoModalProps = {
  closeModal: () => void;
  editingTodo: TodoItem | undefined;
};

const AddAndEditTodoModal = ({
  closeModal,
  editingTodo,
}: AddAndEditTodoModalProps) => {
  const [isRemindTimeSelectorOpen, setIsRemindTimeSelectorOpen] =
    useState(false);
  const [remindTimeHour, setRemindTimeHour] = useState(0);
  const [remindTimeMinute, setRemindTimeMinute] = useState(0);
  const [isRepeatTimeSelectorOpen, setIsRepeatTimeSelectorOpen] =
    useState(false);
  const [repeatTimes, setRepeatTimes] = useState<number[]>([]);
  const [showMask, setShowMask] = useState(false);

  const modalRef = useRef<HTMLDivElement | null>(null);
  const remindTimeRef = useRef<HTMLDivElement | null>(null);
  const repeatTimeRef = useRef<HTMLDivElement | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<TodoItem>({
    resolver: zodResolver(TodoItemSchema),
    defaultValues: {
      title: '',
      remindTime: '00:00',
      repeat: [],
    },
  });

  const handleFormSubmit: SubmitHandler<TodoItem> = async (formValues) => {
    const method = editingTodo ? 'patch' : 'post';
    try {
      await axiosWithAuth({
        method,
        url: '/ezcon/api/todoList',
        data: formValues,
      });
      message.success('儲存成功!');
      closeModal();
    } catch (error) {
      const errObj = error.response.data;
      const errMsg = errObj.displayMessage || '儲存失敗!';
      asyncErrorHandler(errObj, errMsg, 'Error-handleEditTodoSubmit:');
    }
  };

  const handleSelectMenuClose = ({
    event,
    ref,
    type,
  }: {
    event: MouseEvent;
    ref: React.MutableRefObject<HTMLElement | null>;
    type: 'remindTime' | 'repeatTime';
  }) => {
    if (ref.current && !ref.current.contains(event.target as Node)) {
      type === 'remindTime'
        ? setIsRemindTimeSelectorOpen(false)
        : setIsRepeatTimeSelectorOpen(false);
    }
  };

  useEffect(() => {
    window.addEventListener('click', (event) =>
      handleSelectMenuClose({ event, ref: remindTimeRef, type: 'remindTime' })
    );
    window.addEventListener('click', (event) =>
      handleSelectMenuClose({ event, ref: repeatTimeRef, type: 'repeatTime' })
    );

    return () => {
      window.removeEventListener('click', (event) =>
        handleSelectMenuClose({ event, ref: remindTimeRef, type: 'remindTime' })
      );
      window.removeEventListener('click', (event) =>
        handleSelectMenuClose({ event, ref: repeatTimeRef, type: 'repeatTime' })
      );
    };
  }, []);

  useEffect(() => {
    if (editingTodo) {
      Object.keys(editingTodo).forEach((key: keyof TodoItem) => {
        setValue(key, editingTodo[key]);
      });
      const remindTimeHourString = editingTodo.remindTime.split(':')[0];
      const remindTimeMinuteString = editingTodo.remindTime.split(':')[1];
      setRemindTimeHour(Number(remindTimeHourString));
      setRemindTimeMinute(Number(remindTimeMinuteString));
      setRepeatTimes(editingTodo.repeat);
    }
  }, [editingTodo, setValue]);

  const updateRemindTime = (type: 'hour' | 'minute', value: number) => {
    if (type === 'hour') {
      setRemindTimeHour(value);
    }
    if (type === 'minute') {
      setRemindTimeMinute(value);
    }
    const newRemindTimeHour = type === 'hour' ? value : remindTimeHour;
    const newRemindTimeMinute = type === 'minute' ? value : remindTimeMinute;
    const remindTime = formatRemindTime(newRemindTimeHour, newRemindTimeMinute);
    setValue('remindTime', remindTime);
  };

  const updateRepeatTimes = (newRepeatTimes: number[]) => {
    setRepeatTimes(newRepeatTimes);
    setValue('repeat', repeatTimes);
  };

  useEffect(() => {
    const remindTime = formatRemindTime(remindTimeHour, remindTimeMinute);
    setValue('remindTime', remindTime);
  }, [remindTimeHour, remindTimeMinute, setValue]);

  useEffect(() => {
    setValue('repeat', repeatTimes);
  }, [repeatTimes, setValue]);

  const displayRepeatTimes = getDisplayRepeatTimes(repeatTimes);

  const isLandscape = useOrientation() === 'LANDSCAPE';

  useEffect(() => {
    if (!isLandscape) {
      setShowMask(false);
      return;
    }
    if (isRemindTimeSelectorOpen || isRepeatTimeSelectorOpen) {
      modalRef.current?.scrollTo(0, 0);
      setShowMask(true);
    } else {
      setShowMask(false);
    }
  }, [isLandscape, isRemindTimeSelectorOpen, isRepeatTimeSelectorOpen]);

  return (
    <div
      className={
        isLandscape && showMask ? styles['modal-scroll-disabled'] : styles.modal
      }
      ref={modalRef}
    >
      <form className={styles.form} onSubmit={handleSubmit(handleFormSubmit)}>
        <div className={styles.formItem}>
          <label htmlFor="title">標題</label>
          <input
            type="text"
            {...register('title', { required: true })}
            placeholder="例如:社區會議"
            className={errors.title ? styles.inputError : undefined}
          />
          <p className={styles.errorMsg}>
            {errors.title ? errors.title.message : ''}
          </p>
        </div>
        <div
          className={`${styles.formItem} ${styles.relative} ${styles.selector}`}
          ref={remindTimeRef}
        >
          <label
            htmlFor="remindTime"
            onClick={() =>
              setIsRemindTimeSelectorOpen((prevState) => !prevState)
            }
          >
            提醒時間
          </label>
          <input type="time" {...register('remindTime')} />
          <div
            className={
              isRemindTimeSelectorOpen
                ? styles['displayTextContainer-focused']
                : styles.displayTextContainer
            }
            onClick={() =>
              setIsRemindTimeSelectorOpen((prevState) => !prevState)
            }
          >
            <p>{formatRemindTime(remindTimeHour, remindTimeMinute)}</p>
          </div>
          {isLandscape ? <div className={styles.space} /> : null}
          {isRemindTimeSelectorOpen ? (
            <HourAndMinuteSelector
              remindTimeHour={remindTimeHour}
              remindTimeMinute={remindTimeMinute}
              updateRemindTime={updateRemindTime}
            />
          ) : null}
        </div>
        <div
          className={`${styles.formItem} ${styles.relative} ${styles.selector}`}
          ref={repeatTimeRef}
        >
          <label
            htmlFor="repeat"
            onClick={() =>
              setIsRepeatTimeSelectorOpen((prevState) => !prevState)
            }
          >
            重複
          </label>
          <input
            {...register('repeat')}
            placeholder="永不"
            className={styles.repeatTimeInput}
          />
          <div
            className={
              isRepeatTimeSelectorOpen
                ? `${styles['displayTextContainer-focused']} ${styles.withArrowIcon}`
                : `${styles.displayTextContainer} ${styles.withArrowIcon}`
            }
            onClick={() =>
              setIsRepeatTimeSelectorOpen((prevState) => !prevState)
            }
          >
            <p>{displayRepeatTimes}</p>
            <img src="/icon/ic_arrow_down.svg" alt="downward arrow" />
          </div>
          {isRepeatTimeSelectorOpen ? (
            <RepeatTimeSelector
              repeatTimes={repeatTimes}
              updateRepeatTimes={updateRepeatTimes}
              closeSelector={() => setIsRepeatTimeSelectorOpen(false)}
            />
          ) : null}
        </div>
        {isLandscape ? null : (
          <MainButton className={styles.submitBtn} type="submit">
            儲存
          </MainButton>
        )}
      </form>
      <div className={styles.footerContainer}>
        <MainButton onClick={closeModal}>離開</MainButton>
        {isLandscape ? (
          <MainButton
            className={styles.submitBtn}
            onClick={handleSubmit(handleFormSubmit)}
          >
            儲存
          </MainButton>
        ) : null}
      </div>
      {showMask ? <div className={styles.mask} /> : null}
    </div>
  );
};

export default AddAndEditTodoModal;
